import React, { useEffect, useRef, useState } from "react";
import { TFieldDefClass } from "../types/api/APIClassTypes";
import { TReservationSearchOptions } from "../types/components/SearchOptions";
import { RequestSearch } from "../models/RequestSearch";
import { Limits } from "../models/Limits";
import {
    TListCalendarInjectedProps,
    TListCalendarWrapperProps,
} from "../types/components/ListCalendarWrapper";
import { MillenniumDate, MillenniumDateTime } from "@timeedit/millennium-time";
import ReservationListSettings from "./ReservationListSettings";
import Language from "../lib/Language";
import _ from "underscore";
import TemplateKind from "../models/TemplateKind";
import ReservationTypeList from "./ReservationTypeList";
import RC from "../lib/ReservationConstants";
import API from "../lib/TimeEditAPI";
import { Reservation } from "../models/Reservation";
import { useAppContext } from "../contexts/AppContext";

const COL_IDS = {
    ID: 0,
    DATE: 1,
    TIME: 2,
    TYPES: 3,
    FIELDS: 4,
    CREATED: 5,
    MODIFIED: 6,
    WEEK: 8,
    WEEKDAY: 9,
    LENGTH: 10,
    STATUS: 11,
    TAGS: 13,
};

const getAvailableColumns = (context) => {
    const columns = [
        { name: Language.get("cal_reservation_list_column_id"), sortable: true, id: COL_IDS.ID },
        {
            name: Language.get("cal_reservation_list_column_date"),
            sortable: true,
            id: COL_IDS.DATE,
        },
        {
            name: Language.get("cal_reservation_list_column_time"),
            sortable: false,
            id: COL_IDS.TIME,
        },
        {
            name: Language.get("cal_reservation_list_column_type_all"),
            sortable: false,
            id: COL_IDS.TYPES,
        },
        {
            name: Language.get("cal_reservation_list_column_field_all"),
            sortable: false,
            id: COL_IDS.FIELDS,
        },
        {
            name: Language.get("cal_reservation_list_column_created"),
            sortable: true,
            id: COL_IDS.CREATED,
        },
        {
            name: Language.get("cal_reservation_list_column_modified"),
            sortable: true,
            id: COL_IDS.MODIFIED,
        },
        {
            name: Language.get("cal_reservation_list_column_week"),
            sortable: false,
            id: COL_IDS.WEEK,
        },
        {
            name: Language.get("cal_reservation_list_column_week_day"),
            sortable: false,
            id: COL_IDS.WEEKDAY,
        },
        {
            name: Language.get("cal_reservation_list_column_length"),
            sortable: false,
            id: COL_IDS.LENGTH,
        },
        {
            name: Language.get("cal_reservation_list_column_status"),
            sortable: false,
            id: COL_IDS.STATUS,
        },
        { name: Language.get("nc_tags_title"), sortable: true, id: COL_IDS.TAGS },
        {
            name: Language.get("nc_reservation_group_list_title"),
            sortable: false,
            id: 14,
        },
    ];
    if (true) {
        // TODO Check if timezone column should be active
        columns.push({
            name: Language.get("cal_res_side_user_time_zone"),
            sortable: false,
            id: 15,
        });
    }
    return columns;
};

type TRequestListProps = TListCalendarWrapperProps<RequestSearch> & TListCalendarInjectedProps;

const DEFAULT_TIME_LIMITS = () => ({
    begin: MillenniumDateTime.fromDate(MillenniumDate.today()).getMts(),
    end: 0,
});

// `data` arrives via ListCalendar's cloneElement, so it is absent when the child
// is rendered directly. The class relied on the try/catch swallowing the
// resulting TypeError; the guard makes that explicit.
const getTimeLimits = (data?: { limits?: Limits }) => {
    if (!data?.limits) {
        return DEFAULT_TIME_LIMITS();
    }
    try {
        return {
            begin: MillenniumDateTime.fromDate(data.limits.getStartDate()).getMts(),
            end: MillenniumDateTime.fromDate(data.limits.getEndDate()).getMts(),
        };
    } catch (error) {
        return DEFAULT_TIME_LIMITS();
    }
};

const limitTo = (categories, allowedCategories) =>
    _.omit(
        categories,
        (value, category) =>
            !_.some(
                allowedCategories,
                (allowedCategory) =>
                    String(allowedCategory.id) === category &&
                    _.every(value, (val) => _.contains(allowedCategory.categories, val))
            )
    );

const RequestList = (props: TRequestListProps) => {
    const appContext = useAppContext();

    // One state object: componentDidMount passed this.setState straight to
    // API.getObjectCategories, relying on the class's partial merge.
    const [state, setState] = useState(() => {
        const timeLimit = getTimeLimits(props.data);
        return {
            validStatus: [] as number[],
            allStatus: [RC.STATUS.REQUESTED, RC.STATUS.REJECTED],
            searchOptions: {
                templateKind: TemplateKind.RESERVATION,
                searchString: "",
                beginTime: timeLimit.begin,
                endTime: timeLimit.end,
                name: "",
                allReservations: true,
                reservationStatus: [RC.STATUS.REQUESTED, RC.STATUS.REJECTED],
                includeMembers: RC.MEMBERS.EXCLUDE,
                useTemplateGroup: false,
                templateGroup: 0,
                reservationVariant: RC.VARIANT.RESERVATION,
                complete: false,
                incomplete: true,
                permission: RC.PERMISSION.READ,
                objectCategories: {},
                reservationCategories: {},
                placeholderType: null,
            } as TReservationSearchOptions,
            availableReservationCategories: [] as TFieldDefClass[],
            availableReservationCheckboxes: [] as TFieldDefClass[],
            availableObjectCategories: [] as TFieldDefClass[],
            availableObjectCheckboxes: [] as TFieldDefClass[],
        };
    });

    useEffect(() => {
        API.getObjectCategories((result) => setState((prev) => ({ ...prev, ...result })));
    }, []);

    // componentDidUpdate re-synced the time window when data.limits changed
    // identity. Its inner comparison read timeLimits.endTime, which getTimeLimits
    // never returns (it returns { begin, end }), so the guard was always true.
    // Corrected to match ConflictList, which is the only copy that had it right.
    const prevLimits = useRef(props.data?.limits);
    useEffect(() => {
        if (prevLimits.current === props.data?.limits) {
            return;
        }
        prevLimits.current = props.data?.limits;
        const timeLimits = getTimeLimits(props.data);
        setState((prev) => {
            if (
                timeLimits.begin === prev.searchOptions.beginTime &&
                timeLimits.end === prev.searchOptions.endTime
            ) {
                return prev;
            }
            return {
                ...prev,
                searchOptions: _.extend({}, prev.searchOptions, {
                    beginTime: timeLimits.begin,
                    endTime: timeLimits.end,
                }),
            };
        });
    });

    const onSelectionChanged = (newSelectedReservations) => {
        const status = _.uniq(
            _.flatten(
                newSelectedReservations.map((reservation) => {
                    if (reservation.status) {
                        return reservation.status.map((st) => st.status);
                    }
                    return [RC.STATUS.CONFIRMED];
                })
            ).filter((st) => st >= RC.STATUS.CONFIRMED && st < RC.STATUS.TIME)
        );
        Reservation.getValidStatus(status, (result) => {
            setState((prev) => ({ ...prev, validStatus: result.map((st) => st.status) }));
        });
    };

    // No current caller supplies `cb`; kept for signature compatibility.
    const onSearchSettingsChange = (settings, cb?: () => void) => {
        let newSettings = _.extend(
            {},
            _.omit(settings, (value) => value === undefined)
        );
        // Request list should always search for reservations, not cancellations
        newSettings = _.extend(newSettings, {
            reservationVariant: RC.VARIANT.RESERVATION,
            complete: true,
            incomplete: true,
        });

        // Handle no selected status as both requested and rejected
        if (newSettings.reservationStatus && newSettings.reservationStatus.length === 0) {
            newSettings = _.extend(newSettings, {
                reservationStatus: [RC.STATUS.REQUESTED, RC.STATUS.REJECTED],
            });
        } else if (newSettings.reservationStatus) {
            newSettings = _.extend(newSettings, {
                reservationStatus: _.filter(
                    newSettings.reservationStatus,
                    (status) => status === RC.STATUS.REQUESTED || status === RC.STATUS.REJECTED
                ),
            });
        }

        setState((prev) => ({
            ...prev,
            searchOptions: _.extend({}, prev.searchOptions, newSettings),
        }));
        if (cb) {
            cb();
        }
    };

    const getSettingsComponent = (
        saveDefaultSettings,
        saveSettings,
        getSavedSettings,
        removeSettings
    ) => {
        const opts = state.searchOptions;

        return (
            <ReservationListSettings
                isRequestList={true}
                onChange={onSearchSettingsChange}
                allReservations={opts.allReservations}
                reservationStatus={opts.reservationStatus}
                includeMembers={opts.includeMembers}
                saveDefaultSettings={saveDefaultSettings}
                saveSettings={saveSettings}
                removeSettings={removeSettings}
                getSavedSettings={getSavedSettings}
                templateKind={opts.templateKind}
                complete={opts.complete}
                incomplete={opts.incomplete}
                permission={opts.permission}
                useTemplateGroup={opts.useTemplateGroup}
                objectCategories={limitTo(
                    opts.objectCategories,
                    state.availableObjectCategories.concat(state.availableObjectCheckboxes)
                )}
                reservationCategories={limitTo(
                    opts.reservationCategories,
                    state.availableReservationCategories.concat(
                        state.availableReservationCheckboxes
                    )
                )}
                availableReservationCategories={state.availableReservationCategories}
                availableReservationCheckboxes={state.availableReservationCheckboxes}
                availableObjectCategories={state.availableObjectCategories}
                availableObjectCheckboxes={state.availableObjectCheckboxes}
                placeholderType={opts.placeholderType}
            />
        );
    };

    const classes = {
        requestList: true,
    };

    return (
        <ReservationTypeList
            {...props}
            allowMultiSelection={true}
            onSearchSettingsChange={onSearchSettingsChange}
            onSelectionChanged={onSelectionChanged}
            searchOptions={state.searchOptions}
            defaultStatus={RC.STATUS.TIME}
            allStatus={state.allStatus}
            validStatus={state.validStatus}
            columns={getAvailableColumns(appContext)}
            defaultSelectedColumns={[COL_IDS.DATE, COL_IDS.TIME, COL_IDS.TYPES, COL_IDS.STATUS]}
            getSettingsComponent={getSettingsComponent}
            title={Language.get("nc_request_list_title")}
            classes={classes}
            supportsEmail
            supportsBatchOperations={true}
            addTypeColumns={true}
        />
    );
};

export default RequestList;
